/**
 * An aquarium: a ticket at the desk (a day's ticket lets you back in), then the halls: the big reef tank, the touch
 * pool where you can stroke a ray, the tunnel through the shark tank, the dark room of jellyfish. Each is a little
 * fun the first time and calm after; seeing them all is a day out.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const AQUARIUMS: Record<string, string> = {
  lagos: 'Blue Lagoon Aquarium, Victoria Island', us: 'Harbor Aquarium, Manhattan', france: 'Océanorium, Paris',
  china: 'Pearl Tide Aquarium, Shanghai', japan: 'Shiokaze Aquarium, Tokyo', italy: 'Acquario del Tirreno, Rome', uae: 'Coral Dome Aquarium, Dubai',
};
export const aquariumFor = (region: string): string => AQUARIUMS[region] ?? AQUARIUMS.lagos;
export const aquariumTicket = (s: LifeState) => localPrice(s, 9_000, 500);

export type AquaThing = 'reef' | 'touch' | 'tunnel' | 'jellies';
interface AquaState { day?: number; done?: AquaThing[]; visits?: number }
const of = (s: LifeState): AquaState => ((s as LifeState & { aqua?: AquaState }).aqua ??= {});

export const hasAquaTicket = (s: LifeState) => of(s).day === Math.floor(s.minutes / 1440);

export function buyAquaTicket(s: LifeState): { ok: boolean; line: string } {
  if (hasAquaTicket(s)) return { ok: true, line: 'Your wristband from earlier still works.' };
  if (!spend(s, aquariumTicket(s), 'Aquarium ticket')) return { ok: false, line: "You can't afford a ticket." };
  const a = of(s);
  a.day = Math.floor(s.minutes / 1440); a.done = []; a.visits = (a.visits ?? 0) + 1;
  if (a.visits === 1) s.history.push({ t: s.minutes, text: 'A first trip to the aquarium.' });
  return { ok: true, line: 'A blue wristband with a shark on it. Through the doors, the light goes blue.' };
}

const LINES: Record<AquaThing, [string, string]> = {
  reef: ['You sit on the bench in front of the big tank and lose twenty minutes to the fish.', 'Back on the bench by the reef tank. The same parrotfish goes past, twice.'],
  touch: ['Two fingers in the cold water, and a ray glides up under your hand: smooth, like wet velvet. It comes back for more.', 'The rays know the drill: one slides under your fingers and away.'],
  tunnel: ['Under the glass, a shark passes right over your head, its belly white, its eye on you. A ray flies over like a plane.', 'Through the tunnel again. A turtle paddles over, unbothered.'],
  jellies: ['The dark room: jellyfish pulsing slowly in blue and violet light. You breathe slower without meaning to.', 'Back with the jellies. Slow, slow, slow.'],
};

export function atAquarium(s: LifeState, what: AquaThing): { ok: boolean; line: string; events: LifeEvent[]; all: boolean } {
  if (!hasAquaTicket(s)) return { ok: false, line: 'Get a ticket at the desk first.', events: [], all: false };
  const a = of(s), done = (a.done ??= []), first = !done.includes(what);
  if (first) done.push(what);
  s.needs.fun = Math.min(100, s.needs.fun + (first ? 8 : 2));
  if (what === 'jellies' || what === 'reef') s.needs.energy = Math.min(100, s.needs.energy + 3); // (it's calming)
  const all = first && done.length === 4;
  if (all) { s.needs.social = Math.min(100, s.needs.social + 4); s.history.push({ t: s.minutes, text: 'Saw everything at the aquarium.' }); }
  return { ok: true, line: LINES[what][first ? 0 : 1] + (all ? ' That\'s all of it: a proper day out.' : ''), events: advance(s, first ? 15 : 6, { busy: true }), all };
}
